import { cn } from '../../lib/cn';
import { OUTCOME_LABEL } from './api';
import type { PartyCallOutcome } from './api';

/**
 * The outcome pill — overnight v20 §D2.
 *
 * ===========================================================================
 * THE TONES ARE A JUDGEMENT AND THEY ARE STATED, NOT DECORATION
 * ===========================================================================
 * The house reserves the DANGER pair for failures — a failed test round, a refused save.
 * Nothing a call log records is a failure of the system, so nothing here wears it. What
 * the pills do carry:
 *
 *   `unspecified`        NEUTRAL. It claims nothing, so it looks like nothing. It is the
 *                        enum's honest degenerate (migration 56) and must never be
 *                        painted as a problem — "nobody said" is not "it went badly".
 *   `no_answer`          NEUTRAL. Not reaching a customer is ordinary.
 *   `callback_requested` WARNING. Something is OWED to the customer — the one state in
 *                        this vocabulary that leaves work outstanding, and the reason a
 *                        reader scans this column at all.
 *   `escalated`          WARNING. Also outstanding, one desk over.
 *   `connected` /
 *   `resolved`           OK. Contact made / matter closed.
 *
 * ⚠ NO FALL-THROUGH THAT PRINTS THE SERVER'S OWN WORD. An unrecognised token renders the
 * NEUTRAL pill with the raw token as its label and nothing else — the visit register
 * learned this the expensive way when a masked value arrived and the default printed the
 * redaction marker as a pill label. Here the map is exhaustive over the closed six by
 * type, so the fallback is unreachable through the typed API and exists only for a wire
 * that has drifted ahead of this build.
 */
const TONE: Readonly<Record<PartyCallOutcome, string>> = {
  unspecified: 'bg-subtle text-fg ring-1 ring-line',
  no_answer: 'bg-subtle text-fg ring-1 ring-line',
  connected: 'bg-ok-bg text-ok-fg',
  callback_requested: 'bg-warn-bg text-warn-fg',
  resolved: 'bg-ok-bg text-ok-fg',
  escalated: 'bg-warn-bg text-warn-fg',
};

const NEUTRAL = 'bg-subtle text-fg ring-1 ring-line';

export function OutcomePill({
  outcome,
}: {
  outcome: PartyCallOutcome | string;
}): React.JSX.Element {
  const known = outcome in TONE;
  const label = known ? OUTCOME_LABEL[outcome as PartyCallOutcome] : outcome;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-pill px-2 py-0.5 text-label font-medium',
        known ? TONE[outcome as PartyCallOutcome] : NEUTRAL,
      )}
    >
      {label}
    </span>
  );
}
